import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  contatos as api,
  type AtendimentoNaBusca,
  type ContatoNaBusca,
  type LadoDoContato,
} from '../api/contatos'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'

/**
 * União manual de identidades (demanda 069).
 *
 * 🔴 **A sugestão automática continua conservadora — isto não a substitui.**
 * `UnificacaoDeContatos` só sugere pares com dado em comum parcial, e semelhança
 * de nome nunca é sugerida. Sugestão é o sistema afirmando; união manual é a
 * **pessoa** afirmando, e as duas carregam responsabilidades diferentes.
 *
 * A porta existe porque Telegram e Instagram **não fornecem telefone** — e o id
 * do Instagram é escopado por aplicativo. Sem ela, todo contato desses canais
 * nasce fragmentado para sempre, e o atendente vê a mesma pessoa como duas.
 */
export function UniaoManual({
  podeUnir,
  aoConcluir,
  Lado,
}: {
  podeUnir: boolean
  aoConcluir: () => void | Promise<void>
  Lado: (props: { lado: LadoDoContato }) => React.ReactElement
}) {
  const { t } = useTranslation()

  const [um, setUm] = useState<ContatoNaBusca | null>(null)
  const [outro, setOutro] = useState<ContatoNaBusca | null>(null)
  const [lados, setLados] = useState<LadoDoContato[] | null>(null)
  const [justificativa, setJustificativa] = useState('')
  const [unindo, setUnindo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const escolher = async (lado: 'um' | 'outro', contato: ContatoNaBusca) => {
    setErro(null)
    setRecado(null)

    const proximoUm = lado === 'um' ? contato : um
    const proximoOutro = lado === 'outro' ? contato : outro

    if (lado === 'um') setUm(contato)
    else setOutro(contato)

    setLados(null)

    // Os dois lados à vista antes de qualquer botão: sem isso ninguém decide,
    // só clica — e unir a pessoa errada é incidente de privacidade.
    if (proximoUm && proximoOutro && proximoUm.id !== proximoOutro.id) {
      try {
        setLados(await api.lados(proximoUm.id, proximoOutro.id))
      } catch {
        setLados([])
      }
    }
  }

  const mesmoContato = um !== null && outro !== null && um.id === outro.id
  const absorvido = [um, outro].find((c) => c?.vinculadoAoContatoId != null) ?? null
  const prontos = um !== null && outro !== null && !mesmoContato && absorvido === null

  return (
    <Cartao titulo={t('contatos.manual.titulo')}>
      <p className="usuarios__descricao">{t('contatos.manual.descricao')}</p>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}

      <div className="contatos__colunas">
        <BuscaDeContato
          rotulo={t('contatos.manual.primeiro')}
          escolhido={um}
          aoEscolher={(c) => escolher('um', c)}
          aoLimpar={() => { setUm(null); setLados(null) }}
        />
        <BuscaDeContato
          rotulo={t('contatos.manual.segundo')}
          escolhido={outro}
          aoEscolher={(c) => escolher('outro', c)}
          aoLimpar={() => { setOutro(null); setLados(null) }}
        />
      </div>

      {/* UI-11: cada estado da decisão diz o que fazer em seguida. */}
      {um === null && outro === null && (
        <Estado tipo="vazio" titulo={t('contatos.manual.comeceAqui')} descricao={t('contatos.manual.comeceAquiAjuda')} />
      )}

      {((um === null) !== (outro === null)) && (
        <Aviso tom="info">{t('contatos.manual.faltaUmLado')}</Aviso>
      )}

      {mesmoContato && <Aviso tom="perigo">{t('contatos.manual.mesmoContato')}</Aviso>}

      {absorvido !== null && (
        <Aviso tom="perigo">
          {t('contatos.manual.jaAbsorvido', {
            nome: absorvido.nome ?? `#${absorvido.id}`,
            outro: absorvido.vinculadoAoContatoId,
          })}
        </Aviso>
      )}

      {prontos && (
        <>
          {lados === null ? (
            <Estado tipo="carregando" />
          ) : (
            <div className="contatos__colunas">
              {lados.map((lado) => (
                <Lado key={lado.id} lado={lado} />
              ))}
            </div>
          )}

          <label className="contatos__justificativa">
            <span className="contatos__subtitulo">{t('contatos.manual.justificativa')}</span>
            <input
              type="text"
              className="campo__entrada"
              value={justificativa}
              maxLength={200}
              placeholder={t('contatos.manual.justificativaExemplo')}
              onChange={(e) => setJustificativa(e.target.value)}
            />
            <small className="usuarios__descricao">{t('contatos.manual.justificativaAjuda')}</small>
          </label>

          {podeUnir && (
            <div className="usuarios__acoes-formulario">
              <Botao
                variante="primario"
                carregando={unindo}
                onClick={async () => {
                  // 🔴 Confirmação explícita, com o efeito nomeado — a mesma da
                  // união sugerida. A porta manual não relaxa nenhuma trava.
                  const segue = window.confirm(
                    t('contatos.confirmarUniao', {
                      um: um!.nome ?? `#${um!.id}`,
                      outro: outro!.nome ?? `#${outro!.id}`,
                    }),
                  )

                  if (!segue) return

                  setErro(null)
                  setUnindo(true)

                  try {
                    await api.unir(um!.id, outro!.id, justificativa.trim() || undefined)
                    setRecado(t('contatos.unidos'))
                    setUm(null)
                    setOutro(null)
                    setLados(null)
                    setJustificativa('')
                    await aoConcluir()
                  } catch (e) {
                    const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
                    setErro(corpo?.detail ?? t('comum.erroDescricao'))
                  } finally {
                    setUnindo(false)
                  }
                }}
              >
                {t('contatos.manual.unir')}
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  )
}

/**
 * Busca de um lado. Aceita nome ou identificador — mas o vazio ensina a buscar
 * pelo **identificador**, que é o que distingue duas pessoas de mesmo nome.
 */
function BuscaDeContato({
  rotulo,
  escolhido,
  aoEscolher,
  aoLimpar,
}: {
  rotulo: string
  escolhido: ContatoNaBusca | null
  aoEscolher: (contato: ContatoNaBusca) => void
  aoLimpar: () => void
}) {
  const { t } = useTranslation()

  const [termo, setTermo] = useState('')
  const [achados, setAchados] = useState<ContatoNaBusca[] | null>(null)
  const [buscando, setBuscando] = useState(false)

  const buscar = async () => {
    if (termo.trim().length < 2) return

    setBuscando(true)
    try {
      setAchados(await api.buscar(termo.trim()))
    } catch {
      setAchados([])
    } finally {
      setBuscando(false)
    }
  }

  return (
    <section className="contatos__lado">
      <h3 className="contatos__nome">{rotulo}</h3>

      {escolhido ? (
        <>
          <p className="usuarios__descricao">
            <strong>{escolhido.nome ?? t('contatos.semNome', { id: escolhido.id })}</strong>
            {' · '}
            {t('contatos.conversas', { total: escolhido.conversas })}
          </p>
          <Botao variante="secundario" onClick={aoLimpar}>
            {t('contatos.manual.trocar')}
          </Botao>
        </>
      ) : (
        <>
          <div className="contatos__busca">
            <input
              type="search"
              className="campo__entrada"
              aria-label={rotulo}
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void buscar()
              }}
              placeholder={t('contatos.manual.buscarPlaceholder')}
            />
            <Botao variante="secundario" carregando={buscando} onClick={buscar}>
              {t('contatos.manual.buscar')}
            </Botao>
          </div>

          {achados !== null && achados.length === 0 && (
            <Estado
              tipo="vazio"
              titulo={t('contatos.manual.semAchados')}
              descricao={t('contatos.manual.semAchadosAjuda')}
            />
          )}

          {achados !== null && achados.length > 0 && (
            <ul className="contatos__itens">
              {achados.map((c) => (
                <li key={c.id}>
                  <button type="button" className="contatos__achado" onClick={() => aoEscolher(c)}>
                    <strong>{c.nome ?? t('contatos.semNome', { id: c.id })}</strong>
                    <small>
                      {c.identificadores.map((i) => `${i.tipo} ${i.valor}`).join(' · ') ||
                        t('contatos.manual.semIdentificador')}
                    </small>
                    {c.vinculadoAoContatoId != null && (
                      <small className="contatos__absorvido">{t('contatos.manual.marcaAbsorvido')}</small>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Vínculo manual com o acervo migrado (critério 5 da 069).
 *
 * `VinculoDeAcervo` e `ComoCasou.Manual` já existiam — o que não existia era
 * quem os escrevesse. O casamento automático só liga o que casa por número, e
 * contato de Instagram ou Telegram não casa com número nenhum.
 */
export function VinculoComOAcervo({ podeVincular }: { podeVincular: boolean }) {
  const { t } = useTranslation()

  const [contato, setContato] = useState<ContatoNaBusca | null>(null)
  const [termo, setTermo] = useState('')
  const [achados, setAchados] = useState<AtendimentoNaBusca[] | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const buscar = async () => {
    if (termo.trim().length < 2) return

    setBuscando(true)
    try {
      setAchados(await api.buscarNoAcervo(termo.trim()))
    } catch {
      setAchados([])
    } finally {
      setBuscando(false)
    }
  }

  return (
    <Cartao titulo={t('contatos.acervo.titulo')}>
      <p className="usuarios__descricao">{t('contatos.acervo.descricao')}</p>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}

      <div className="contatos__colunas">
        <BuscaDeContato
          rotulo={t('contatos.acervo.contato')}
          escolhido={contato}
          aoEscolher={setContato}
          aoLimpar={() => setContato(null)}
        />

        <section className="contatos__lado">
          <h3 className="contatos__nome">{t('contatos.acervo.atendimento')}</h3>

          <div className="contatos__busca">
            <input
              type="search"
              className="campo__entrada"
              aria-label={t('contatos.acervo.atendimento')}
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void buscar()
              }}
              placeholder={t('contatos.acervo.buscarPlaceholder')}
            />
            <Botao variante="secundario" carregando={buscando} onClick={buscar}>
              {t('contatos.manual.buscar')}
            </Botao>
          </div>

          {achados !== null && achados.length === 0 && (
            <Estado
              tipo="vazio"
              titulo={t('contatos.acervo.semAchados')}
              descricao={t('contatos.acervo.semAchadosAjuda')}
            />
          )}

          {achados !== null && achados.length > 0 && (
            <ul className="contatos__itens">
              {achados.map((a) => (
                <li key={a.protocolo}>
                  <span>
                    <strong>{a.nome ?? t('contatos.acervo.semNome')}</strong>{' '}
                    <small>
                      {a.numero ?? '—'} · {t('contatos.acervo.mensagens', { total: a.mensagens })}
                      {a.data && ` · ${formatar.dataHora(a.data)}`}
                    </small>
                  </span>

                  {a.contatoId != null ? (
                    <small>{t('contatos.acervo.jaVinculado', { contato: a.contatoId })}</small>
                  ) : (
                    podeVincular &&
                    contato !== null && (
                      <Botao
                        variante="secundario"
                        onClick={async () => {
                          setErro(null)
                          try {
                            await api.vincularAcervo(contato.id, a.protocolo)
                            setRecado(t('contatos.acervo.vinculado'))
                            setAchados(
                              achados.map((x) =>
                                x.protocolo === a.protocolo ? { ...x, contatoId: contato.id } : x,
                              ),
                            )
                          } catch (e) {
                            const corpo =
                              e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
                            setErro(corpo?.detail ?? t('comum.erroDescricao'))
                          }
                        }}
                      >
                        {t('contatos.acervo.vincular')}
                      </Botao>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}

          {contato === null && achados !== null && achados.length > 0 && (
            <Aviso tom="info">{t('contatos.acervo.escolhaOContato')}</Aviso>
          )}
        </section>
      </div>
    </Cartao>
  )
}
